/* Quaza Price Chart — shared core for the PDP market-history widget and the
   Price Tracker page. Dependency-free IIFE; data comes from the Quaza prices
   Worker (see /pages/price-tracker). Wealthsimple-style chart adapted to the
   storefront's light theme: no axes/gridlines, 2px line, gradient area,
   dashed high/low guides, range pills, pointer scrubbing. */
(function () {
  'use strict';
  if (window.QuazaPriceChart) return;

  var UP = '#5c9765';
  var DOWN = '#d74a5d';
  var RANGES = [
    { key: '1w', label: '1W' },
    { key: '1m', label: '1M' },
    { key: '3m', label: '3M' },
    { key: '6m', label: '6M' },
    { key: 'max', label: 'MAX' }
  ];
  var RANGE_TEXT = { '1w': 'past week', '1m': 'past month', '3m': 'past 3 months', '6m': 'past 6 months', 'max': 'all time' };

  /* Money display — mirrors the iOS app's PriceConversion: market data stays
     CAD cents end-to-end; the worker's daily CAD→currency `rates` map (shipped
     inside /catalog and /prices responses) converts at display time only.
     No rate for the requested code → stay CAD; never mislabel a market price. */
  var moneyCode = 'CAD', moneyRate = 1;
  var moneyFmt = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' });
  function setMoney(code, rate) {
    code = (code || '').toUpperCase();
    if (!code || code === 'CAD' || !(rate > 0)) {
      moneyCode = 'CAD'; moneyRate = 1;
      moneyFmt = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' });
      return;
    }
    try {
      var f = new Intl.NumberFormat('en-CA', { style: 'currency', currency: code });
      moneyFmt = f; moneyCode = code; moneyRate = rate;
    } catch (e) { /* unknown ISO code: keep CAD */ }
  }
  function fmtCAD(cents) { return cents == null ? '—' : moneyFmt.format((cents / 100) * moneyRate); }
  function fmtDate(iso) {
    var d = new Date(iso + 'T12:00:00Z');
    return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  }
  function fmtDateLong(iso) {
    var d = new Date(iso + 'T12:00:00Z');
    return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  }

  var CSS = '' +
    '.qpc,.qpc *,.qpc *::before,.qpc *::after{box-sizing:border-box}' +
    '.qpc{font-family:var(--typeBasePrimary,Roboto),sans-serif;color:#0f0f0f}' +
    '.qpc-head{display:flex;flex-wrap:wrap;align-items:baseline;gap:6px 14px;margin:0 0 6px}' +
    '.qpc-price{font-family:var(--typeHeaderPrimary,Montserrat),sans-serif;font-weight:700;font-size:28px;letter-spacing:-0.01em}' +
    '.qpc-change{font-size:14px;font-weight:500}' +
    '.qpc-change.qpc-up{color:' + UP + '}' +
    '.qpc-change.qpc-down{color:' + DOWN + '}' +
    '.qpc-chartwrap{position:relative;height:220px;margin:4px 0 2px;touch-action:pan-y}' +
    '.qpc-chartwrap svg{display:block;width:100%;height:100%}' +
    '.qpc-hl{position:absolute;font-size:11px;color:#6b6b66;background:rgba(255,255,255,0.85);padding:0 4px;border-radius:3px;pointer-events:none;white-space:nowrap}' +
    '.qpc-scrub{position:absolute;top:0;bottom:22px;width:1px;background:#0f0f0f;opacity:0.35;display:none;pointer-events:none}' +
    '.qpc-dot{position:absolute;width:11px;height:11px;border-radius:999px;border:2.5px solid #fff;box-shadow:0 1px 4px rgba(15,15,15,0.3);transform:translate(-50%,-50%);display:none;pointer-events:none;z-index:2}' +
    '.qpc-tip{position:absolute;top:-4px;transform:translate(-50%,-100%);background:#0f0f0f;color:#fff;font-size:12px;line-height:1.35;padding:5px 9px;border-radius:6px;white-space:nowrap;display:none;pointer-events:none;z-index:2}' +
    '.qpc-dates{display:flex;justify-content:space-between;font-size:11px;color:#9a9a94;margin:0 0 10px}' +
    '.qpc-ranges{display:flex;gap:6px;flex-wrap:wrap}' +
    '.qpc-pill{font-family:inherit;font-size:12px;font-weight:600;letter-spacing:0.04em;padding:6px 14px;border-radius:999px;border:1px solid #e8e8e1;background:#fff;color:#6b6b66;cursor:pointer;transition:none}' +
    '.qpc-pill[aria-checked="true"]{background:#0f0f0f;border-color:#0f0f0f;color:#fff}' +
    '.qpc-pill:focus-visible{outline:2px solid ' + UP + ';outline-offset:2px}' +
    '.qpc-meta{font-size:11px;color:#9a9a94;margin-top:12px}' +
    '.qpc-meta a{white-space:nowrap}' +
    '.qpc-skel{background:linear-gradient(90deg,#f4f4f1 25%,#ecece7 50%,#f4f4f1 75%);background-size:200% 100%;border-radius:8px}' +
    '.qpc-empty{font-size:13px;color:#6b6b66;padding:24px 0}';

  function injectCSS() {
    if (document.getElementById('qpc-css')) return;
    var s = document.createElement('style');
    s.id = 'qpc-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  function downsample(series, max) {
    if (series.length <= max) return series;
    var out = [], step = (series.length - 1) / (max - 1);
    for (var i = 0; i < max; i++) out.push(series[Math.round(i * step)]);
    return out;
  }

  /* Redraw any chart whose mount changes size (late stylesheet, rotation,
     responsive reflow). Every drawn wrap stores its args; one shared observer
     re-renders on meaningful change. This is what keeps charts correct on
     cold loads where CSS lands after the first draw. */
  var sizeRO = 'ResizeObserver' in window
    ? new ResizeObserver(function (entries) {
        entries.forEach(function (e) {
          var wrap = e.target;
          var a = wrap.__qpcArgs;
          if (!a) return;
          var w = wrap.clientWidth, h = wrap.clientHeight;
          if (Math.abs(w - (a.drawnW || 0)) < 8 && Math.abs(h - (a.drawnH || 0)) < 8) return;
          cancelAnimationFrame(wrap.__qpcRaf || 0);
          wrap.__qpcRaf = requestAnimationFrame(function () {
            drawChart(wrap, a.rawSeries, a.pos, a.opts);
          });
        });
      })
    : null;

  /* Renders the SVG chart (+ high/low guides + scrub layer) into wrap.
     series: [[isoDate, value], ...]  pos: gain (true) or loss (false).
     opts: { minimal: true  — no guides/labels/scrub (sparkline-style cards)
             fmt: fn(value) — value formatter (defaults to CAD cents) } */
  function drawChart(wrap, rawSeries, pos, opts) {
    opts = opts || {};
    var fmt = opts.fmt || fmtCAD;

    wrap.__qpcArgs = { rawSeries: rawSeries, pos: pos, opts: opts };
    if (sizeRO && !wrap.__qpcObserved) { wrap.__qpcObserved = true; sizeRO.observe(wrap); }

    // Cold-load guard: if our stylesheet hasn't laid the mount out yet, wait a
    // few frames instead of baking a bogus viewBox (the giant-chart bug).
    if (wrap.clientWidth < 40 || wrap.clientHeight < 30) {
      wrap.__qpcWaits = (wrap.__qpcWaits || 0) + 1;
      if (wrap.__qpcWaits < 40) {
        cancelAnimationFrame(wrap.__qpcRaf || 0);
        wrap.__qpcRaf = requestAnimationFrame(function () {
          drawChart(wrap, rawSeries, pos, opts);
        });
        return;
      }
    }
    wrap.__qpcWaits = 0;

    wrap.textContent = '';
    var series = downsample(rawSeries, 180);
    var W = Math.max(wrap.clientWidth, 120);
    var H = Math.max(wrap.clientHeight, 60) || 220;
    wrap.__qpcArgs.drawnW = wrap.clientWidth;
    wrap.__qpcArgs.drawnH = wrap.clientHeight;
    var padTop = opts.minimal ? 4 : 16, padBottom = opts.minimal ? 4 : 22, padX = 2;
    var innerH = H - padTop - padBottom;

    var min = Infinity, max = -Infinity, i;
    for (i = 0; i < series.length; i++) {
      var c = series[i][1];
      if (c < min) min = c;
      if (c > max) max = c;
    }
    if (!isFinite(min)) return;
    var span = max - min || max * 0.02 || 1;

    var n = series.length;
    var xs = new Array(n), ys = new Array(n);
    for (i = 0; i < n; i++) {
      xs[i] = n === 1 ? W / 2 : padX + (i / (n - 1)) * (W - padX * 2);
      ys[i] = padTop + (1 - (series[i][1] - min) / span) * innerH;
    }

    var line = '';
    for (i = 0; i < n; i++) line += (i ? 'L' : 'M') + xs[i].toFixed(1) + ' ' + ys[i].toFixed(1);
    var area = line + 'L' + xs[n - 1].toFixed(1) + ' ' + (H - padBottom) + 'L' + xs[0].toFixed(1) + ' ' + (H - padBottom) + 'Z';

    var color = pos ? UP : DOWN;
    var gid = 'qpcg' + Math.random().toString(36).slice(2, 8);
    var first = series[0][1], last = series[n - 1][1];
    var pct = first > 0 ? Math.round(((last - first) / first) * 1000) / 10 : null;
    var label = 'Chart: ' + fmt(last) +
      (pct != null ? ', ' + (pct >= 0 ? 'up ' : 'down ') + Math.abs(pct) + '%' : '') +
      ' from ' + fmtDateLong(series[0][0]) + ' to ' + fmtDateLong(series[n - 1][0]) +
      '. High ' + fmt(max) + ', low ' + fmt(min) + '.';

    var yMax = padTop + (1 - (max - min) / span) * innerH;
    var yMin = padTop + innerH;

    var svgNS = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('viewBox', '0 0 ' + W + ' ' + H);
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.setAttribute('role', 'img');
    svg.setAttribute('aria-label', label);
    // inline sizing so the chart can NEVER outgrow its mount, even if the
    // stylesheet is still in flight on a cold load
    svg.style.display = 'block';
    svg.style.width = '100%';
    svg.style.height = '100%';
    var showGuides = !opts.minimal && opts.guides !== false;
    var guides = !showGuides ? '' :
      '<line x1="0" y1="' + yMax.toFixed(1) + '" x2="' + W + '" y2="' + yMax.toFixed(1) + '" stroke="#d8d8d1" stroke-width="1" stroke-dasharray="3 4"/>' +
      '<line x1="0" y1="' + yMin.toFixed(1) + '" x2="' + W + '" y2="' + yMin.toFixed(1) + '" stroke="#d8d8d1" stroke-width="1" stroke-dasharray="3 4"/>';
    svg.innerHTML =
      '<defs><linearGradient id="' + gid + '" x1="0" y1="0" x2="0" y2="1">' +
      '<stop offset="0" stop-color="' + color + '" stop-opacity="0.14"/>' +
      '<stop offset="1" stop-color="' + color + '" stop-opacity="0"/>' +
      '</linearGradient></defs>' +
      guides +
      '<path d="' + area + '" fill="url(#' + gid + ')"/>' +
      '<path d="' + line + '" fill="none" stroke="' + color + '" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>' +
      (opts.minimal ? '' : '<circle cx="' + xs[n - 1].toFixed(1) + '" cy="' + ys[n - 1].toFixed(1) + '" r="3" fill="' + color + '"/>');
    wrap.appendChild(svg);

    if (opts.minimal) return;

    if (showGuides) {
      var hi = document.createElement('span');
      hi.className = 'qpc-hl';
      hi.textContent = 'H ' + fmt(max);
      hi.style.top = Math.max(yMax - 16, 0) + 'px';
      hi.style.right = '0';
      wrap.appendChild(hi);

      var lo = document.createElement('span');
      lo.className = 'qpc-hl';
      lo.textContent = 'L ' + fmt(min);
      lo.style.top = Math.min(yMin + 4, H - padBottom - 12) + 'px';
      lo.style.right = '0';
      wrap.appendChild(lo);
    }

    // Scrubbing: hairline + dot pinned to the line. Desktop: hover. Touch:
    // press-and-drag with pointer capture (vertical swipes still scroll — the
    // wrap keeps touch-action: pan-y, so the browser cancels us for scrolls).
    // With opts.onScrub the host updates its own header (Robinhood-style);
    // otherwise a small floating readout follows the hairline. Elements are
    // rebuilt per draw but listeners bind ONCE per wrap, reading live state —
    // redraws (resize self-heal) must not stack handlers.
    var scrub = document.createElement('div');
    scrub.className = 'qpc-scrub';
    wrap.appendChild(scrub);
    var dot = document.createElement('div');
    dot.className = 'qpc-dot';
    dot.style.background = color;
    wrap.appendChild(dot);
    var tip = null;
    if (!opts.onScrub) {
      tip = document.createElement('div');
      tip.className = 'qpc-tip';
      wrap.appendChild(tip);
    }
    wrap.__qpcState = {
      series: series, xs: xs, ys: ys, n: n, W: W, H: H, padX: padX, first: first,
      fmt: fmt, opts: opts, scrub: scrub, dot: dot, tip: tip
    };

    if (!wrap.__qpcBound) {
      wrap.__qpcBound = true;
      var onMove = function (ev) {
        var st = wrap.__qpcState;
        if (!st) return;
        var rect = wrap.getBoundingClientRect();
        var x = ev.clientX - rect.left;
        var idx = Math.round(((x - st.padX) / Math.max(rect.width - st.padX * 2, 1)) * (st.n - 1));
        idx = Math.max(0, Math.min(st.n - 1, idx));
        var px = (st.xs[idx] / st.W) * rect.width;
        var py = (st.ys[idx] / st.H) * rect.height;
        st.scrub.style.left = px + 'px';
        st.scrub.style.display = 'block';
        st.dot.style.left = px + 'px';
        st.dot.style.top = py + 'px';
        st.dot.style.display = 'block';
        if (st.opts.onScrub) {
          st.opts.onScrub({ date: st.series[idx][0], value: st.series[idx][1], first: st.first, index: idx });
        } else if (st.tip) {
          st.tip.style.left = Math.max(52, Math.min(rect.width - 52, px)) + 'px';
          st.tip.style.display = 'block';
          st.tip.innerHTML = '<strong>' + st.fmt(st.series[idx][1]) + '</strong> · ' + fmtDate(st.series[idx][0]);
        }
      };
      var onEnd = function () {
        var st = wrap.__qpcState;
        if (!st) return;
        st.scrub.style.display = 'none';
        st.dot.style.display = 'none';
        if (st.opts.onScrub) st.opts.onScrub(null);
        else if (st.tip) st.tip.style.display = 'none';
      };
      wrap.addEventListener('pointerdown', function (ev) {
        if (ev.pointerType !== 'mouse') { try { wrap.setPointerCapture(ev.pointerId); } catch (e) {} }
        onMove(ev);
      });
      wrap.addEventListener('pointermove', onMove, { passive: true });
      wrap.addEventListener('pointerup', onEnd, { passive: true });
      wrap.addEventListener('pointercancel', onEnd, { passive: true });
      wrap.addEventListener('pointerleave', onEnd, { passive: true });
    }
  }

  /* Full widget: stats head, chart, date footer, range pills, meta line.
     opts: { endpoint  — URL without query, e.g. .../prices/by-handle/<handle>
             detail    — optional preloaded response for the initial range
             range     — initial range key (default '3m')
             trackerUrl— optional href for a "full history" link
             onDetail  — optional callback(detail) after each successful load } */
  function renderWidget(mount, opts) {
    injectCSS();
    if (opts.currency) {
      setMoney(opts.currency, opts.detail && opts.detail.rates
        ? opts.detail.rates[(opts.currency || '').toUpperCase()] : null);
    }
    var range = opts.range || '3m';
    var cache = {};
    var root = document.createElement('div');
    root.className = 'qpc';
    root.innerHTML =
      '<div class="qpc-head"><span class="qpc-price"></span><span class="qpc-change"></span></div>' +
      '<div class="qpc-chartwrap"></div>' +
      '<div class="qpc-dates"><span class="qpc-d0"></span><span class="qpc-d1"></span></div>' +
      '<div class="qpc-ranges" role="radiogroup" aria-label="Price history range"></div>' +
      '<div class="qpc-meta"></div>';
    mount.textContent = '';
    mount.appendChild(root);

    var head = root.querySelector('.qpc-head');
    var priceEl = root.querySelector('.qpc-price');
    var changeEl = root.querySelector('.qpc-change');
    var chartEl = root.querySelector('.qpc-chartwrap');
    var d0 = root.querySelector('.qpc-d0');
    var d1 = root.querySelector('.qpc-d1');
    var rangesEl = root.querySelector('.qpc-ranges');
    var metaEl = root.querySelector('.qpc-meta');

    RANGES.forEach(function (r) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'qpc-pill';
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', r.key === range ? 'true' : 'false');
      b.dataset.range = r.key;
      b.textContent = r.label;
      b.addEventListener('click', function () { setRange(r.key); });
      rangesEl.appendChild(b);
    });

    var lastDetail = null;
    function paint(detail) {
      lastDetail = detail;
      var series = detail.series || [];
      if (!series.length) {
        chartEl.innerHTML = '<div class="qpc-empty">No price history for this range yet.</div>';
        priceEl.textContent = fmtCAD(detail.current_cents);
        changeEl.textContent = '';
        d0.textContent = d1.textContent = '';
        return;
      }
      var pos = (detail.change_abs_cents || 0) >= 0;
      var sign = pos ? '+' : '−';
      var abs = Math.abs(detail.change_abs_cents || 0);
      var pct = detail.change_pct != null ? Math.abs(detail.change_pct) : null;
      var restingChange = sign + fmtCAD(abs) +
        (pct != null ? ' (' + sign + pct + '%)' : '') + ' · ' + (RANGE_TEXT[range] || range);
      function rest() {
        priceEl.textContent = fmtCAD(detail.current_cents);
        changeEl.className = 'qpc-change ' + (pos ? 'qpc-up' : 'qpc-down');
        changeEl.textContent = restingChange;
      }
      rest();
      drawChart(chartEl, series, pos, {
        // scrubbing drives the header readout (works the same on touch + mouse)
        onScrub: function (pt) {
          if (!pt) { rest(); return; }
          priceEl.textContent = fmtCAD(pt.value);
          var d = pt.value - pt.first;
          changeEl.className = 'qpc-change ' + (d >= 0 ? 'qpc-up' : 'qpc-down');
          changeEl.textContent = (d >= 0 ? '+' : '−') + fmtCAD(Math.abs(d)) +
            (pt.first > 0 ? ' (' + (d >= 0 ? '+' : '−') + Math.abs(Math.round((d / pt.first) * 1000) / 10) + '%)' : '') +
            ' · ' + fmtDate(pt.date);
        }
      });
      d0.textContent = fmtDate(series[0][0]);
      d1.textContent = fmtDate(series[series.length - 1][0]);
      metaEl.innerHTML = 'Market price · TCGplayer · CAD · Updated daily' +
        (opts.trackerUrl ? ' · <a href="' + opts.trackerUrl + '">Full history →</a>' : '');
      if (opts.onDetail) opts.onDetail(detail);
    }

    function setRange(key) {
      range = key;
      rangesEl.querySelectorAll('.qpc-pill').forEach(function (p) {
        p.setAttribute('aria-checked', p.dataset.range === key ? 'true' : 'false');
      });
      load();
    }

    function load() {
      if (cache[range]) return paint(cache[range]);
      chartEl.innerHTML = '<div class="qpc-skel" style="height:100%"></div>';
      fetch(opts.endpoint + '?range=' + range, { headers: { Accept: 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
        .then(function (detail) { cache[range] = detail; paint(detail); })
        .catch(function () {
          chartEl.innerHTML = '<div class="qpc-empty">Couldn’t load price history. ' +
            '<button type="button" class="qpc-pill" data-retry>Retry</button></div>';
          var rb = chartEl.querySelector('[data-retry]');
          if (rb) rb.addEventListener('click', load);
        });
    }

    if (opts.detail && (opts.detail.series || []).length >= 0) {
      cache[opts.range || '3m'] = opts.detail;
    }
    load();

    // resize self-healing lives in drawChart's shared observer
    return { setRange: setRange, destroy: function () { mount.textContent = ''; } };
  }

  window.QuazaPriceChart = {
    renderWidget: renderWidget,
    drawChart: drawChart,
    fmtCAD: fmtCAD,
    fmtDate: fmtDate,
    setMoney: setMoney,
    moneyCode: function () { return moneyCode; },
    injectCSS: injectCSS
  };
})();
